/**
 * What the create overlay can open. The value is what goes in the URL (`?create=member`), so it is stable,
 * lowercase and never translated. Add a kind here, then describe it in `CREATE_KIND_META` and register its form
 * in `create-overlay-registry.tsx` (see docs/guides/create-overlay.md).
 */
export enum CreateKind {
  Member = "member",
  Visitor = "visitor",
  Minister = "minister",
  Offering = "offering",
  Expense = "expense",
  Transfer = "transfer",
  TransferRequest = "transfer-request",
  Pledge = "pledge",
  Budget = "budget",
  Activity = "activity",
  Asset = "asset",
}

/** The search param that opens the overlay over the current page. */
export const CREATE_PARAM = "create"

/** Router state key carrying the optional prefill passed to `open(kind, prefill)`. */
export const CREATE_PREFILL_STATE = "createPrefill"

/** Prefill keys shared by entry points, old-route redirects and forms. */
export enum CreatePrefillKey {
  /** A pledge: the activity to preselect (the old create page's `?activityId=`). */
  ActivityId = "activityId",
  /** A transfer request: the saved calculation to start from (the old create page's `?saved=`). */
  SavedTransferCalculation = "saved",
}

/** Values a caller may hand the form on open (ids, names already known), never record contents from the URL. */
export type CreatePrefill = Readonly<Record<string, string>>

/** The two desktop layouts of the approved design. */
export enum CreateOverlayLayout {
  /** "A": the form centred (~680px), for kinds with nothing meaningful to preview. */
  Form = "form",
  /** "C": a wide dialog, the form on the left and a live preview on the right. */
  FormWithPreview = "form_with_preview",
}

/** The facts the permission rules are written against (the same checks as the header "Créer" menu). */
export type CreateAccessFacts = {
  canEditMembers: boolean
  canEditMinistries: boolean
  isParishContext: boolean
  /** Activities EDIT (what the old `/activities/create` route and the menu asked). */
  canEditActivities: boolean
  /** Assets EDIT with the Assets feature flag on (the old `/assets/items/new` route and the menu). */
  canCreateAssets: boolean
  /** Finance EDIT and the TransactionCreate action (what the old finance create routes and the menu asked). */
  canCreateTransactions: boolean
  /** Budgeting EDIT and the BudgetingEdit action. */
  canEditBudgets: boolean
  /** The Pledges / Budgeting feature flags (paused modules close every door at once). */
  pledgesEnabled: boolean
  budgetingEnabled: boolean
  /** Branch-to-branch transfer requests need another branch to address (`useShowMultiBranchWorkspaceUi`). */
  isMultiBranchWorkspace: boolean
}

/** Footer and preview wording a kind may swap for the defaults (keys under `createOverlay`). */
export enum CreateOverlayLabelKey {
  /** "En saisir une autre après": money entries are entered, not created. */
  EnterAnother = "enterAnother",
  /** "Ce qui sera enregistré": the finance preview shows what the ledger will record. */
  PreviewRecorded = "previewRecorded",
}

export type CreateKindMeta = {
  layout: CreateOverlayLayout
  /** The footer checkbox label, when not the default "En créer un autre après". */
  createAnotherLabel?: CreateOverlayLabelKey
  /** The preview panel's heading, when not the default "Aperçu". */
  previewLabel?: CreateOverlayLabelKey
  /** The list page the old create routes redirect to (the overlay opens over it). Branch-relative. */
  listPath: string
  isAllowed: (facts: CreateAccessFacts) => boolean
}

/**
 * Everything about a kind except its form component, so cheap callers (menus, redirects, the palette) can read it
 * without pulling the forms into their bundle. The form lives in the lazy registry.
 */
export const CREATE_KIND_META: Record<CreateKind, CreateKindMeta> = {
  [CreateKind.Member]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/members/list",
    // Members and visitors are branch-level: they do not exist in the parish (system branch).
    isAllowed: (facts) => facts.canEditMembers && !facts.isParishContext,
  },
  [CreateKind.Visitor]: {
    layout: CreateOverlayLayout.Form,
    listPath: "/members/visitors",
    isAllowed: (facts) => facts.canEditMembers && !facts.isParishContext,
  },
  [CreateKind.Minister]: {
    layout: CreateOverlayLayout.Form,
    listPath: "/members/ministries",
    // Minister creation is a ministry-management operation; workspace settings access never stands in for it.
    isAllowed: (facts) => facts.canEditMinistries && !facts.isParishContext,
  },
  // Programs: an activity shows its card (kind, when, department, branch); an asset shows the item as listed.
  [CreateKind.Activity]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/activities",
    isAllowed: (facts) => facts.canEditActivities,
  },
  [CreateKind.Asset]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/assets/items",
    isAllowed: (facts) => facts.canCreateAssets,
  },
  // Finance: the same checks as the old create routes (route policy: TransactionCreate) and the "Créer" menu
  // (finance EDIT, the paused-module flags). Each form still runs its page's own check before drawing fields.
  [CreateKind.Offering]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/finance/transactions",
    createAnotherLabel: CreateOverlayLabelKey.EnterAnother,
    previewLabel: CreateOverlayLabelKey.PreviewRecorded,
    isAllowed: (facts) => facts.canCreateTransactions,
  },
  [CreateKind.Expense]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/finance/transactions",
    createAnotherLabel: CreateOverlayLabelKey.EnterAnother,
    previewLabel: CreateOverlayLabelKey.PreviewRecorded,
    isAllowed: (facts) => facts.canCreateTransactions,
  },
  [CreateKind.Transfer]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/finance/transactions",
    previewLabel: CreateOverlayLabelKey.PreviewRecorded,
    isAllowed: (facts) => facts.canCreateTransactions,
  },
  [CreateKind.TransferRequest]: {
    layout: CreateOverlayLayout.Form,
    listPath: "/finance/transfers",
    isAllowed: (facts) => facts.canCreateTransactions && facts.isMultiBranchWorkspace,
  },
  [CreateKind.Pledge]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/finance/pledges",
    isAllowed: (facts) => facts.canCreateTransactions && facts.pledgesEnabled,
  },
  [CreateKind.Budget]: {
    layout: CreateOverlayLayout.FormWithPreview,
    listPath: "/budgets/list",
    isAllowed: (facts) => facts.canEditBudgets && facts.budgetingEnabled,
  },
}

const KINDS = new Set<string>(Object.values(CreateKind))

/** The kind named by a raw param value, or null for a missing or unknown value (which is then ignored). */
export function parseCreateKind(value: string | null | undefined): CreateKind | null {
  return value && KINDS.has(value) ? (value as CreateKind) : null
}

/** The kind a search string opens (`?create=member&view=list` gives Member). */
export function createKindFromSearch(search: string): CreateKind | null {
  return parseCreateKind(new URLSearchParams(search).get(CREATE_PARAM))
}

/** The kind an app URL opens, if any (`/w/b/members/list?create=member`). */
export function createKindFromUrl(url: string): CreateKind | null {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return null
  const hashStart = url.indexOf("#", queryStart)
  return createKindFromSearch(url.slice(queryStart, hashStart === -1 ? undefined : hashStart))
}

/** `search` with the create param set to `kind` (or removed when `kind` is null), keeping every other param. */
export function withCreateParam(search: string, kind: CreateKind | null): string {
  const params = new URLSearchParams(search)
  if (kind) params.set(CREATE_PARAM, kind)
  else params.delete(CREATE_PARAM)
  const next = params.toString()
  return next ? `?${next}` : ""
}

/** The branch-relative target of an old create route: the kind's list page with the overlay open. */
export function createKindListTarget(kind: CreateKind): string {
  return `${CREATE_KIND_META[kind].listPath}${withCreateParam("", kind)}`
}

export function readCreatePrefill(state: unknown): CreatePrefill | null {
  if (!state || typeof state !== "object" || !(CREATE_PREFILL_STATE in state)) return null
  const prefill = (state as Record<string, unknown>)[CREATE_PREFILL_STATE]
  if (!prefill || typeof prefill !== "object") return null
  const entries = Object.entries(prefill as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  )
  return Object.fromEntries(entries)
}
